import { useRef } from 'react'

import TruncateLines from '@components/TruncateLines'
import { Tag } from '@modules/shortlist/types'
import { Box, Divider, Typography } from '@mui/material'
import { useVirtualizer } from '@tanstack/react-virtual'

import {
  FilterOptionCheckbox,
  FilterOptionContainer,
  FilterOptionLabel,
  ProspectTagDot,
  ProspectTagsContainer,
} from './index.styles'

interface ProspectSectionProps {
  title: string
  selectedIds: string[]
  handleSelect: (id: string) => void
  options: { id: string; label: string; color?: string; tags?: Tag[] }[]
}

const ProspectSection = ({ title, options, handleSelect, selectedIds }: ProspectSectionProps) => {
  const itemHeight = 30
  const parentRef = useRef<HTMLDivElement | null>(null)

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: 12,
  })

  const virtualItems = virtualizer.getVirtualItems()

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} mb="10px">
      <Typography variant="caption">{title}</Typography>
      {title && <Divider sx={{ margin: '10px 0' }} />}
      <Box ref={parentRef} sx={{ height: virtualizer.getTotalSize(), overflow: 'auto' }}>
        <Box
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            }}
          >
            {virtualItems.map(virtualItem => {
              const option = options[virtualItem.index]

              return (
                <Box key={virtualItem.key} data-index={virtualItem.index} style={{ height: `${virtualItem.size}px` }}>
                  <FilterOptionContainer>
                    <FilterOptionLabel variant="body1">
                      <TruncateLines lines={2}>{option.label}</TruncateLines>
                      {!!option.tags?.length && (
                        <ProspectTagsContainer>
                          {option.tags.map(tag => (
                            <ProspectTagDot key={tag.id} $color={tag.color} />
                          ))}
                        </ProspectTagsContainer>
                      )}
                    </FilterOptionLabel>
                    <FilterOptionCheckbox
                      checked={selectedIds.includes(option.id)}
                      onChange={() => handleSelect(option.id)}
                      $color={option.color}
                    />
                  </FilterOptionContainer>
                </Box>
              )
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default ProspectSection
